/*
 * Copyright (c) 2016-2018. campoint AG.
 * This file is licensed under the MIT License (https://opensource.org/licenses/MIT).
 */

import { supportsFileReader, supportsWebSockets } from './capabilities';
import { logger } from './logging/logger';

const POOL_SIZE = 5;
const DATA_HEAD = 'data:image/jpeg;';

interface Loader {
    id: number;
    img: HTMLImageElement;
    frame: number;
    ready: boolean;
}

export class PicStream {
    private counter = 0;
    private lastFrame = 0;

    private source = '';

    private pool: Loader[] = [];
    private timer = 0;

    private handler: (imageElement: HTMLImageElement) => void;
    private target: { src: string };

    private socket: WebSocket;
    private fileReader: FileReader;
    private fallbackTimer: ReturnType<typeof setTimeout>;

    private useWS = true;
    private maxFps: number;

    constructor(target: { src: string } | ((imageElement: HTMLImageElement) => void)) {
        if (typeof target === 'function') {
            this.handler = target;
        } else {
            this.target = target;
        }

        if (supportsFileReader) {
            this.fileReader = new FileReader();
            this.fileReader.onload = (event): void => {
                let data: string = (event.target as any).result;
                if (!data.startsWith(DATA_HEAD)) {
                    data = DATA_HEAD + data.slice(Math.max(0, data.indexOf('base64,')));
                }
                this.loadFrame(data);
            };
        }

        for (let i = 0; i < POOL_SIZE; i++) {
            this.pool.push(this.createLoader(i));
        }
    }

    public play(source: string, fps?: number): void {
        this.stop();
        if (!fps || fps < 0) {
            const res = /fps=(\d+)/.exec(source);
            fps = res ? Number.parseInt(res[1], 10) : 15;
        }
        this.maxFps = fps;
        this.source = source;

        if (supportsWebSockets && this.useWS) {
            this._playWS();
        } else {
            this._play();
        }
    }

    public stop(): void {
        if (this.timer) {
            clearInterval(this.timer);
        }
        if (this.fallbackTimer) {
            clearTimeout(this.fallbackTimer);
        }
        this.timer = this.fallbackTimer = undefined;

        if (this.socket) {
            this.socket.close();
        }

        for (let i = 0; i < POOL_SIZE; i++) {
            this.pool[i].img.src = '';
        }
    }

    private _playWS(): void {
        const wsSource = this.source.replace(/https?:/, 'wss:').replace(/:\d+\//, ':443/');
        let socket: WebSocket;
        try {
            socket = new WebSocket(wsSource);
        } catch (error) {
            // @MDN SECURITY_ERR The port to which the connection is being attempted is being blocked.
            logger.warn('PicStream._playWS SECURITY_ERR', error);
            this.useWS = false;
            this._play();
            return;
        }
        socket.onopen = (): void => {
            socket.send(
                JSON.stringify({
                    action: 'CMDS_STARTVIDEOPULL',
                    params: {
                        fps: this.maxFps,
                        base64: !this.fileReader,
                    },
                })
            );
        };
        socket.onmessage = (ev): void => {
            clearTimeout(this.fallbackTimer);
            socket.onmessage = (event): void => {
                const data = event.data;
                if (typeof data === 'string') {
                    // base64 encoded jpeg image data handling
                    this.loadFrame(DATA_HEAD + 'base64,' + data);
                } else {
                    const fr = this.fileReader;
                    // drop a frame in case the fileReader is too slow
                    if (fr && (fr.readyState === 2 || fr.readyState === 0)) {
                        fr.readAsDataURL(data);
                    }
                }
            };
            socket.onmessage(ev);
        };
        socket.onerror = (event): void => {
            logger.warn(event);
            clearTimeout(this.fallbackTimer);
            this.useWS = false;
            this._play();
        };
        socket.onclose = (): void => {
            logger.log('closed');
            // TODO
        };
        this.fallbackTimer = setTimeout(() => {
            logger.warn('PicStream._playWS fallbackTimer');
            this.useWS = false;
            socket.close(); // TODO code & reason https://developer.mozilla.org/en-US/docs/Web/API/WebSocket#close()
            this._play();
        }, 8000);
        this.socket = socket;
    }

    private _play(): void {
        // TODO 'Dynamically adjusts the framerate by calculating how many frames were properly received in the last check interval'
        if (!this.timer) {
            this.timer = window.setInterval(this.loadFrame.bind(this), 1000 / this.maxFps);
            this.loadFrame();
        }
    }

    private createLoader(id: number): Loader {
        const loader: Loader = { id: id, img: null, frame: 0, ready: true };
        const img = document.createElement('img');
        img.onload = (): void => {
            if (loader.frame > this.lastFrame) {
                this.lastFrame = loader.frame;
                if (this.target) {
                    this.target.src = img.src;
                } else {
                    this.handler(img);
                }
            }
            loader.ready = true;
        };
        img.onerror = (): void => {
            loader.ready = true;
        };
        loader.img = img;
        return loader;
    }

    private loadFrame(source?: string): void {
        for (let i = 0; i < POOL_SIZE; i++) {
            const entry = this.pool[i];
            if (entry.ready || entry.frame <= this.lastFrame) {
                entry.frame = ++this.counter;
                entry.ready = false;
                entry.img.src = source ? source : `${this.source}&_u=${entry.frame}`;
                break;
            }
        }
    }
}
